export const ALLOWED_TYPES = ["plan", "webinar", "ads", "physical"];
export const ALLOWED_BILLING_PERIODS = ["monthly", "quarterly", "yearly", "oneTime"];
export const ALLOWED_STATUSES = ["active", "archived"];
// Admins may discount a line by at most this fraction of its list price.
export const MAX_DISCOUNT_RATIO = 0.2;

export const sumLineItems = (lineItems) =>
  (lineItems || []).reduce((acc, li) => acc + (Number(li.amount) || 0), 0);

// Returns an error string, or null if valid.
export const validateFulfillmentAgainstPricing = (fulfillment, lineItems, basePrice) => {
  if (!fulfillment) return null;
  const sum = sumLineItems(lineItems);
  if (Math.abs(sum - Number(basePrice || 0)) > 0.01) {
    return `Line items (${sum}) must sum to base price (${Number(basePrice || 0)})`;
  }
  const sub = fulfillment.subscription;
  if (sub) {
    if (sub.planKey && !(lineItems || []).some((li) => li.id === "plan")) {
      return `Subscription plan requires a line item with id "plan"`;
    }
    for (const a of sub.addOns || []) {
      // Matched by catalog key, mirroring the backend: grouping by family would
      // make both flow packs share one row.
      const li = (lineItems || []).find((x) => x.id === a.key);
      if (!li) return `Add-on "${a.key}" needs its own price line`;
      // Every add-on bills per unit, not just seats.
      if (!(Number(li.unitPrice) > 0)) return `"${li.title || a.key}" needs a price per unit`;
      if (Math.round(Number(li.amount) * 100) !== Math.round(Number(li.unitPrice) * 100) * Number(a.quantity)) {
        return `"${li.title || a.key}" total must equal unit price × quantity (${li.unitPrice} × ${a.quantity})`;
      }
    }
  }
  return null;
};

// Basics step. Returns { field: message } (empty object = valid).
export const validateBasics = (b) => {
  const e = {};
  if (!b.name || !b.name.trim()) e.name = "Name is required";
  if (!ALLOWED_TYPES.includes(b.type)) e.type = "Type is required";
  if (!ALLOWED_BILLING_PERIODS.includes(b.billing?.period)) e.billingPeriod = "Billing period is required";
  if (!b.billing?.label || !b.billing.label.trim()) e.billingLabel = "Billing label is required";
  if (!b.planTitle || !b.planTitle.trim()) e.planTitle = "Plan title is required";
  if (!b.planSubtitle || !b.planSubtitle.trim()) e.planSubtitle = "Plan subtitle is required";
  const features = b.features || [];
  if (features.length > 15) e.features = "At most 15 features";
  if (features.some((f) => (f || "").length > 80)) e.features = "Each feature must be at most 80 characters";
  return e;
};

// Pricing step. `pricing` = { basePrice, minPrice, currency, migrationDeadline, lineItems }
export const validatePricing = (pricing, { requireStrictMin = false } = {}) => {
  const e = {};
  const base = Number(pricing.basePrice);
  const min = Number(pricing.minPrice);
  if (!Number.isFinite(base)) e.basePrice = "Base price must be a number";
  if (!Number.isFinite(min)) e.minPrice = "Min price must be a number";
  if (Number.isFinite(base) && Number.isFinite(min)) {
    // create allows min <= base; addPricingVersion requires min < base.
    if (requireStrictMin ? !(min < base) : !(min <= base)) {
      e.minPrice = requireStrictMin
        ? "Min price must be less than base price"
        : "Min price must be less than or equal to base price";
    }
  }
  if (!pricing.currency || !pricing.currency.trim()) e.currency = "Currency is required";
  if (pricing.migrationDeadline && new Date(pricing.migrationDeadline) <= new Date()) {
    e.migrationDeadline = "Migration deadline must be in the future";
  }
  const items = pricing.lineItems || [];
  if (items.length > 10) e.lineItems = "At most 10 line items";
  items.forEach((li, i) => {
    if (!li.id || !li.id.trim()) e[`lineItem_${i}_id`] = "id required";
    if (!li.title || !li.title.trim()) e[`lineItem_${i}_title`] = "title required";
    if (li.amount === "" || li.amount === null || li.amount === undefined || !Number.isFinite(Number(li.amount))) e[`lineItem_${i}_amount`] = "amount must be a number";
    // Original is the list price, amount is what the buyer pays. Amount above
    // original is a negative discount: the preview would strike through a price
    // LOWER than the one being charged.
    if (li.originalAmount != null && Number(li.amount) > Number(li.originalAmount) + 0.005) {
      e[`lineItem_${i}_overOriginal`] = "price above original";
    }
    if (li.originalAmount != null && Number(li.amount) < Number(li.originalAmount) * (1 - MAX_DISCOUNT_RATIO) - 0.005) {
      e[`lineItem_${i}_belowFloor`] = "discount exceeds 20%";
    }
  });
  if (Number.isFinite(base) && items.length > 0 && Math.abs(sumLineItems(items) - base) > 0.01) {
    e.lineItemsSum = `Line items (${sumLineItems(items)}) must sum to base price (${base})`;
  }
  return e;
};

// Fulfillment shape (independent of pricing). `f` may be null.
export const validateFulfillmentShape = (f) => {
  const e = {};
  if (!f) return e; // fulfillment is optional
  if (!f.subscription && !f.credits && !f.webinar) {
    e.fulfillment = "Provide at least one of: subscription, credits, webinar";
    return e;
  }
  if (f.subscription) {
    const s = f.subscription;
    if (!["monthly", "quarterly", "yearly"].includes(s.duration)) {
      e.subscription = "Subscription needs a recurring billing period (set it in step 1).";
    } else if (!s.planKey) {
      e.subscription = "Select a base plan — add-ons must accompany a plan.";
    } else if ((s.addOns || []).some((a) => a.family === "team_members" && !(Number(a.quantity) >= 1))) {
      e.subscription = "Team-members needs a seat count of at least 1.";
    }
  }
  if (f.credits) {
    const c = f.credits;
    if (!(Number(c.ctwaAmount) > 0 || Number(c.wccAmount) > 0)) {
      e.credits = "Enter a quantity for at least one credit type.";
    }
  }
  if (f.webinar) {
    try {
      // eslint-disable-next-line no-new
      new window.URL(f.webinar.meetingLink);
    } catch (_) {
      e.webinar = "Meeting link must be a valid URL";
    }
  }
  return e;
};

export const hasErrors = (errObj) => Object.keys(errObj || {}).length > 0;
